// =============================================================================
// domain/accountForm.ts — the Edit Account draft, and what it is allowed to own.
// -----------------------------------------------------------------------------
// ── 🚨 THE FORM IS FIVE FORMS. THIS IS ONE OF THEM ──
//
// The old client's Edit Account modal also carries the firm's COMMISSION TABLE
// (shared by every account at that firm), an "Apply to existing trades" button
// that rewrites logged trades in bulk, an ADJUSTMENTS table that lives in a
// different table and whose "Reset fee" rows move the compliance WINDOW, and a
// metered Recompute-MAE/MFE button that overwrites excursion fields using paid
// market data.
//
// This module owns the one slice whose worst case is confined to a single
// account row: identity, drawdown, profit and position limits. The rest is not
// modelled here, and a draft that cannot express them cannot damage them.
//
// ── 🚨 FOUR RULES HAVE NO FIELD, AND THEY MUST SURVIVE EVERY SAVE ──
//
//   lockMechanic · lockPad · dllEnforcement · consistencyScope
//
// The old client's form has no input for any of them — they are written only by
// applying a saved TEMPLATE — and its save handler REBUILDS `rules` from
// scratch. It shipped that rebuild without two of them, so creating an account
// from a firm shape and then editing ANYTHING silently dropped both on reload,
// at which point its two readers defaulted them OPPOSITELY: the consistency math
// read undefined as "the rule applies", the prop rail read it as "hide the rail".
//
// ⚠ `lockMechanic` is the one that matters most. It decides whether the drawdown
// floor keeps trailing the high-water mark or freezes at the starting balance —
// the difference between an account that can be given back into a breach and one
// that cannot. Losing it silently re-opens the floor.
//
// So `mergeAccountRules` takes the PRIOR rules and carries all four. The
// defaults, when there is no prior value, are the ones that preserve today's
// effective behaviour rather than change it: `'optional'` is how an undefined
// enforcement already behaved at the daily-limit check, and `'always'` is how an
// undefined scope already behaved in the consistency math.
//
// ── ⚠ EVERY NUMERIC FIELD IS A STRING ──
//
// A controlled React input bound to a number cannot hold "" while the user
// clears it, and `value || 0` turns every attempt to type into a re-appearing
// zero. Worse, a `||` fallback moved from uncontrolled DOM into a controlled
// input stops being a display default and becomes an input FILTER — the Goals
// editor made 0 unenterable exactly that way (D108). The draft holds raw text;
// parsing happens once, on save.
// =============================================================================

import type { Account, AccountRules, AccountStatus, AccountType } from './types';
import { feeCadenceOf, type FeeCadence } from './accountFee';

// ---------------------------------------------------------------------------
// The draft
// ---------------------------------------------------------------------------

export interface AccountDraft {
  // identity
  name: string;
  firm: string;
  accountSize: string;
  accountType: AccountType;
  startingBalance: string;
  currentBalance: string;
  autoBalance: boolean;
  excludeFromMetrics: boolean;
  initialPeakBalance: string;
  status: AccountStatus;
  externalId: string;
  /**
   * ── 🔴 THE COST FIELDS, ADDED BECAUSE THEIR ABSENCE WAS AN UNCLEARABLE NUDGE ──
   *
   * A card whose fee is unrecorded shows "N fields missing — Finish setup →".
   * That link opens THIS form, which had no fee input at all, so the prompt
   * could never be cleared from the Accounts tab (#73). The fee was capturable
   * only at creation.
   *
   * ⚠ Two inputs, three stored fields. `free` is the WAIVER, and
   * `domain/accountFee.ts` owns the rule — including the ledger row, which is
   * where the money actually reaches the Economics report.
   */
  feeAmount: string;
  feeCadence: FeeCadence;
  // drawdown
  maxDDType: string;
  maxDDValue: string;
  dailyLossLimit: string;
  // profit & payout
  profitTarget: string;
  consistencyRulePct: string;
  minTradingDays: string;
  payoutSchedule: string;
  payoutsTaken: string;
  // position limits
  maxContracts: string;
  eodFlatRule: boolean;
  scalingRules: string;
  prohibitedProducts: string;
  // ── 🚨 THE FOUR WITH NO FIELD ON THE FORM ──
  //
  // Lock mechanic, lock pad, daily-limit enforcement and consistency scope have
  // no input of their own and are not typed by hand in either client. They ride
  // HERE anyway, seeded from the account, so that:
  //
  //   1. `mergeAccountRules` reads them from ONE place rather than reaching past
  //      the draft into `prior` for a subset of the rules, and
  //   2. **applying a template can change them**, which is the only way they are
  //      ever set — and the whole reason the editor used to tell the user, on
  //      screen, that four of their rules were not editable here.
  //
  // ⚠ Carrying them on the draft rather than stashing them on the account (the
  // old client's `_pendingLockMechanic`) also makes them visible to
  // `draftIsDirty`, so applying a template that changes only the lock mechanic
  // still enables Save. Stashed beside the draft, it would not.
  lockMechanic: string;
  lockPad: string;
  dllEnforcement: string;
  consistencyScope: string;
  /**
   * 🚨 THE FOUR ABOVE ARE ONLY BELIEVED WHEN THIS IS TRUE.
   *
   * Set by `applyTemplateToDraft` and by nothing else. While it is false the
   * merge reads the account's own rules, exactly as it did before templates
   * existed — so a form the user merely opened and edited cannot overwrite them
   * with a snapshot taken when the modal opened.
   *
   * ⚠ THE EXISTING TESTS FOUND THIS. My first version let the draft win
   * unconditionally, which works for the template and quietly re-opens the D62
   * hazard: a value read at open-time and written back at save-time, over
   * whatever happened in between.
   *
   * ⚠ A FLAG, NOT A VALUE COMPARISON. A template that sets the mechanic to what
   * it already was is still an explicit instruction, and a diff could not tell
   * that apart from staleness.
   */
  structuralFromTemplate: boolean;
}

/** `0` prints as "0"; `null` prints as "" so the placeholder shows. */
const numText = (n: number | null | undefined): string =>
  n === null || n === undefined ? '' : String(n);

export function draftFromAccount(a: Account): AccountDraft {
  const r = a.rules;
  return {
    // ⚠ The waiver wins over a stale amount — see `feeCadenceOf`. An account
    // marked free with an old number still on it is showing history, not cost.
    feeAmount: a.feeWaived === true ? '' : numText(a.feeAmount ?? 0),
    feeCadence: feeCadenceOf(a),
    name: a.name ?? '',
    firm: a.firm ?? '',
    accountSize: numText(a.accountSize),
    accountType: a.accountType,
    startingBalance: numText(a.startingBalance),
    currentBalance: numText(a.currentBalance),
    autoBalance: a.autoBalance === true,
    excludeFromMetrics: a.excludeFromMetrics === true,
    // ⚠ BLANK IS A REAL VALUE HERE — "walk the trade timeline and discover the
    // peak" — and it is a different instruction from a peak of zero. `numText`
    // keeps null as "" rather than "0" for exactly that reason.
    initialPeakBalance: numText(a.initialPeakBalance),
    status: a.status,
    externalId: a.externalId ?? '',
    maxDDType: r?.maxDDType ?? 'trailing-intraday',
    maxDDValue: numText(r?.maxDDValue ?? 0),
    dailyLossLimit: numText(r?.dailyLossLimit ?? 0),
    profitTarget: numText(r?.profitTarget ?? 0),
    consistencyRulePct: numText(r?.consistencyRulePct ?? 0),
    minTradingDays: numText(r?.minTradingDays ?? 0),
    payoutSchedule: r?.payoutSchedule ?? '',
    payoutsTaken: numText(r?.payoutsTaken ?? 0),
    maxContracts: numText(r?.maxContracts ?? 0),
    eodFlatRule: r?.eodFlatRule === true,
    scalingRules: r?.scalingRules ?? '',
    prohibitedProducts: (r?.prohibitedProducts ?? []).join(', '),
    // ⚠ Seeded from the account, so an editor that never touches a template
    // round-trips them unchanged — the behaviour the previous version got by
    // reading `prior` inside the merge.
    lockMechanic: r?.lockMechanic ?? '',
    lockPad: numText(r?.lockPad ?? 0),
    dllEnforcement: r?.dllEnforcement ?? 'optional',
    consistencyScope: r?.consistencyScope ?? 'always',
    // ⚠ FALSE on open. Opening the editor is not an instruction about these.
    structuralFromTemplate: false,
  };
}

// ---------------------------------------------------------------------------
// Parsing
// ---------------------------------------------------------------------------

/** Blank, whitespace or unparseable → 0. The field's own validation is what
 *  rejects a bad entry; this is only for values already checked. */
export function numOf(text: string): number {
  const n = Number(String(text).trim());
  return Number.isFinite(n) ? n : 0;
}

/**
 * Blank → NULL, and that is the whole point.
 *
 * ⚠ An initial peak of `null` means "the engine walks the timeline and finds the
 * peak from the trades"; `0` means "this account's high-water mark was zero",
 * which pins the trailing drawdown floor at −maxDD from the start. Collapsing
 * blank to 0 would report an account as failed the moment it opened.
 */
export function numOrNullOf(text: string): number | null {
  const t = String(text).trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

export function parseProhibited(text: string): string[] {
  return String(text).split(',').map((s) => s.trim()).filter(Boolean);
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

export type DraftErrors = Partial<Record<keyof AccountDraft, string>>;

const NUMERIC: readonly (keyof AccountDraft)[] = [
  'accountSize', 'startingBalance', 'currentBalance', 'maxDDValue',
  'dailyLossLimit', 'profitTarget', 'consistencyRulePct', 'minTradingDays',
  'payoutsTaken', 'maxContracts',
];

/**
 * What must be true before this draft may be written.
 *
 * ⚠ THE DAILY LOSS LIMIT IS A POSITIVE NUMBER, and the field says so, because
 * the lock compares today's P&L against its NEGATION. A limit entered as −500
 * would compare `pnl <= -Math.abs(-500)`, which happens to work — and then reads
 * back into the form as a negative, and into "Today's Loss Room" as a negative
 * denominator. Rejected rather than quietly absolute-valued.
 *
 * ⚠ A DRAWDOWN TYPE WITHOUT AN AMOUNT IS NOT AN ERROR. It is the state both of
 * Josh's live accounts are in, and the card already says what is missing. The
 * form's job is to let him fix it, not to refuse to save anything else until he
 * does.
 */
export function validateAccountDraft(d: AccountDraft): DraftErrors {
  const errors: DraftErrors = {};

  if (!d.name.trim()) errors.name = 'A name is required.';

  for (const k of NUMERIC) {
    const t = String(d[k] as string).trim();
    if (t === '') { errors[k] = 'Enter a number, or 0.'; continue; }
    const n = Number(t);
    if (!Number.isFinite(n)) { errors[k] = 'That is not a number.'; continue; }
    if (n < 0) errors[k] = 'This cannot be negative.';
  }

  const peak = String(d.initialPeakBalance).trim();
  if (peak !== '' && !Number.isFinite(Number(peak))) {
    errors.initialPeakBalance = 'That is not a number. Leave it blank to work it out from your trades.';
  }

  if (d.consistencyRulePct && numOf(d.consistencyRulePct) > 100) {
    // Not rejected — a threshold over 100 is enterable in the old client too and
    // is not nonsense — but flagged, because above 100 no amount of trimming the
    // best day can bring the account into compliance and the card will say so.
    errors.consistencyRulePct =
      'Above 100% there is no amount you can trim off a best day to comply. Check the firm’s page.';
  }

  return errors;
}

export function draftIsValid(errors: DraftErrors): boolean {
  return Object.keys(errors).length === 0;
}

/** A blocking error. The consistency warning above is advisory and does not
 *  stop a save — the old client accepts the value and so does this. */
export function blockingErrors(errors: DraftErrors): DraftErrors {
  const { consistencyRulePct: _advisory, ...rest } = errors;
  return rest;
}

// ---------------------------------------------------------------------------
// What the form owns
// ---------------------------------------------------------------------------

export interface OwnedAccountFields {
  name: string;
  firm: string;
  accountType: AccountType;
  accountSize: number;
  startingBalance: number;
  currentBalance: number;
  autoBalance: boolean;
  status: AccountStatus;
  externalId: string;
  initialPeakBalance: number | null;
  excludeFromMetrics: boolean;
}

/**
 * The flat account fields, parsed.
 *
 * ⚠ `currentBalance` IS ALWAYS SENT, and the caller must pass the account's own
 * stored value when auto-calc is on. The field is disabled in that mode — so the
 * draft holds whatever was last computed, and on Josh's Sim1 that is the raw
 * float `50226.25000000001`. Writing it back would persist a rendering artefact
 * as data. See `saveAccount`, which resolves it.
 */
export function ownedAccountFields(d: AccountDraft): OwnedAccountFields {
  return {
    name: d.name.trim(),
    firm: d.firm.trim(),
    accountType: d.accountType,
    accountSize: numOf(d.accountSize),
    startingBalance: numOf(d.startingBalance),
    currentBalance: numOf(d.currentBalance),
    autoBalance: d.autoBalance,
    status: d.status,
    externalId: d.externalId.trim(),
    initialPeakBalance: numOrNullOf(d.initialPeakBalance),
    excludeFromMetrics: d.excludeFromMetrics,
  };
}

/**
 * The whole rules object, with the four field-less rules carried across.
 *
 * ⚠ THE CARRY IS THE POINT OF THIS FUNCTION. Rebuilding `rules` from the form
 * alone is what dropped `dllEnforcement` and `consistencyScope` in the old
 * client — and would drop `lockMechanic` here, which decides whether the
 * drawdown floor freezes. `prior` is the rules as LOADED, never a re-parse of
 * the draft.
 */
export function mergeAccountRules(
  d: AccountDraft, prior: AccountRules | null | undefined,
): AccountRules {
  return {
    maxDDType: (d.maxDDType || null) as AccountRules['maxDDType'],
    maxDDValue: numOf(d.maxDDValue),
    // ── 🚨 CARRIED FROM THE ACCOUNT, UNLESS A TEMPLATE SAID OTHERWISE ──
    //
    // The form has no fields for these four, so by default they are read from
    // `prior` and the draft cannot touch them — which is what stops a snapshot
    // taken when the modal opened from overwriting a change made since (D62).
    //
    // Applying a template is the one explicit instruction to change them, and
    // it sets `structuralFromTemplate`. Nothing else does.
    ...(d.structuralFromTemplate
      ? {
        lockMechanic: (d.lockMechanic || null) as AccountRules['lockMechanic'],
        lockPad: numOf(d.lockPad),
        dllEnforcement: (d.dllEnforcement || 'optional') as AccountRules['dllEnforcement'],
        consistencyScope: (d.consistencyScope || 'always') as AccountRules['consistencyScope'],
      }
      : {
        lockMechanic: prior?.lockMechanic ?? null,
        lockPad: prior?.lockPad ?? 0,
        dllEnforcement: prior?.dllEnforcement ?? 'optional',
        consistencyScope: prior?.consistencyScope ?? 'always',
      }),
    // ── owned ──
    dailyLossLimit: numOf(d.dailyLossLimit),
    profitTarget: numOf(d.profitTarget),
    consistencyRulePct: numOf(d.consistencyRulePct),
    minTradingDays: numOf(d.minTradingDays),
    payoutSchedule: d.payoutSchedule,
    payoutsTaken: numOf(d.payoutsTaken),
    maxContracts: numOf(d.maxContracts),
    eodFlatRule: d.eodFlatRule,
    scalingRules: d.scalingRules,
    prohibitedProducts: parseProhibited(d.prohibitedProducts),
  };
}

/** Did anything the form owns actually change? Used to skip a pointless write
 *  — and, more usefully, to warn before discarding real edits. */
export function draftIsDirty(a: Account, d: AccountDraft): boolean {
  const base = draftFromAccount(a);
  return (Object.keys(base) as (keyof AccountDraft)[]).some((k) => base[k] !== d[k]);
}

// ---------------------------------------------------------------------------
// Option lists
// ---------------------------------------------------------------------------

export const ACCOUNT_TYPE_OPTIONS: readonly { value: AccountType; label: string }[] = [
  { value: 'combine', label: 'Combine (eval)' },
  { value: 'sim-funded', label: 'Sim-funded (XFA/PA)' },
  { value: 'live-funded', label: 'Live-funded' },
  { value: 'funded', label: 'Funded (legacy)' },
  { value: 'personal', label: 'Personal' },
];

export const STATUS_OPTIONS: readonly { value: AccountStatus; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'passed', label: 'Passed' },
  { value: 'blown', label: 'Blown' },
  { value: 'closed', label: 'Closed' },
];

export const MAX_DD_TYPE_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'trailing-intraday', label: 'Trailing intraday' },
  { value: 'trailing-eod', label: 'Trailing EOD' },
  { value: 'static', label: 'Static' },
];

// ---------------------------------------------------------------------------
// Applying a saved template to an open edit form
// ---------------------------------------------------------------------------

/** What a template needs to carry to be applicable. Structural subset of `PropTemplate`. */
export interface ApplicableTemplate {
  label: string;
  accountSize: number | null;
  rules: AccountRules | null;
}

export interface ApplyTemplateResult {
  draft: AccountDraft;
  /** Set when the template moved the account size — see below. */
  sizeChangedTo: number | null;
}

/**
 * Fill an open edit form from a saved template.
 *
 * 🚨 NOTHING IS WRITTEN. This returns a new draft; the user still presses Save.
 * The old client says so in its own hint and it is the property that makes an
 * Apply button safe to press out of curiosity.
 *
 * ── 🚨 THREE THINGS IT DELIBERATELY DOES NOT DO ──
 *
 * **1. It never touches `payoutsTaken`.** That is this account's actual payout
 * history, not a rule. A template rewriting it would silently reset the count
 * the consistency and payout-velocity maths depend on. The old client calls this
 * out at the same line, and it is the single most dangerous field on the form to
 * get wrong because nothing downstream would look odd.
 *
 * **2. It never touches identity** — name, firm, external id, status, balances.
 * A template describes a PLAN, not an account.
 *
 * **3. It does not blank a field the template has nothing to say about.**
 * `payoutSchedule` and `scalingRules` are free text; a template saved from an
 * account that had neither carries empty strings, and copying those over would
 * quietly erase notes the user typed. Only non-empty values are applied — which
 * mirrors the old client's `if (R.payoutSchedule) set(...)`.
 *
 * ⚠ **ACCOUNT SIZE COMES ALONG, AND IS REPORTED SEPARATELY.** It is a property
 * of the plan the template describes, so it belongs — but it moves the drawdown
 * baseline every gauge on the Accounts tab is measured against, which is too big
 * a change to make silently. `sizeChangedTo` exists so the caller can say it.
 */
export function applyTemplateToDraft(
  draft: AccountDraft,
  tpl: ApplicableTemplate,
): ApplyTemplateResult {
  const r = tpl.rules;
  if (!r) return { draft, sizeChangedTo: null };

  const next: AccountDraft = { ...draft };

  next.maxDDType = r.maxDDType ?? next.maxDDType;
  next.maxDDValue = numText(r.maxDDValue ?? 0);
  next.dailyLossLimit = numText(r.dailyLossLimit ?? 0);
  next.profitTarget = numText(r.profitTarget ?? 0);
  next.consistencyRulePct = numText(r.consistencyRulePct ?? 0);
  next.minTradingDays = numText(r.minTradingDays ?? 0);
  next.maxContracts = numText(r.maxContracts ?? 0);
  next.eodFlatRule = r.eodFlatRule === true;

  // ⚠ Free text: only when the template actually has some. See (3) above.
  if (r.payoutSchedule) next.payoutSchedule = r.payoutSchedule;
  if (r.scalingRules) next.scalingRules = r.scalingRules;
  if (r.prohibitedProducts?.length) {
    next.prohibitedProducts = r.prohibitedProducts.join(', ');
  }

  // 🚨 THE FOUR THE FORM CANNOT TYPE. This is the only path that sets them, and
  // the reason they were moved onto the draft at all.
  next.lockMechanic = r.lockMechanic ?? '';
  next.lockPad = numText(r.lockPad ?? 0);
  next.dllEnforcement = r.dllEnforcement ?? 'optional';
  next.consistencyScope = r.consistencyScope ?? 'always';
  // 🚨 The one place this is ever set. Without it the four above are ignored by
  // `mergeAccountRules`, which reads the account's own rules by default.
  next.structuralFromTemplate = true;

  let sizeChangedTo: number | null = null;
  const size = Number(tpl.accountSize);
  if (Number.isFinite(size) && size > 0 && numText(size) !== draft.accountSize) {
    next.accountSize = numText(size);
    sizeChangedTo = size;
  }

  return { draft: next, sizeChangedTo };
}
